import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../services/firebase';
import { AlertDialog } from '../components/common';
import { useAuth } from '../hooks/useAuth';

const GoogleCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthLoading } = useAuth(); // 🔧 로딩 스피너 제어를 위해 추가

  // AlertDialog 상태 (랜딩페이지의 resendSuccessModal 패턴과 동일)
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

  // AlertDialog 표시 함수
  const showAlert = (type, title, message) => {
    setAlertDialog({
      open: true,
      type,
      title,
      message
    });
  };

  // AlertDialog 닫기 함수
  const closeAlert = () => {
    setAlertDialog({
      open: false,
      type: 'error',
      title: '',
      message: ''
    });
    // AlertDialog를 닫을 때 메인 페이지로 이동
    navigate('/');
  };

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log('🔄 [GoogleCallback] 구글 로그인 콜백 처리 시작');
        
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');
        const email = urlParams.get('email');
        const name = urlParams.get('name');
        const given_name = urlParams.get('given_name');
        const family_name = urlParams.get('family_name');
        const picture = urlParams.get('picture');
        const locale = urlParams.get('locale');
        const email_verified = urlParams.get('email_verified') === 'true';
        const google_id = urlParams.get('google_id');
        const provider = urlParams.get('provider');
        const merged = urlParams.get('merged') === 'true';
        const error = urlParams.get('error');

        // 🔧 로딩 스피너 종료 (에러든 성공이든 콜백 처리 시작 시 종료)
        if (setAuthLoading) {
          setAuthLoading(false);
          console.log('🔄 [GoogleCallback] 구글 로그인 로딩 스피너 종료');
        }

        if (error) {
          console.error('Google 로그인 에러:', error);
          showAlert('error', 'Google 로그인 실패', decodeURIComponent(error));
          return;
        }

        if (!token) {
          console.error('No token received from Google OAuth');
          showAlert('error', 'Google 로그인 실패', '토큰을 받지 못했습니다.');
          return;
        }

        // Firebase Custom Token으로 로그인
        const userCredential = await signInWithCustomToken(auth, token);
        
        if (userCredential.user) {
          console.log('Google 로그인 성공:', {
            uid: userCredential.user.uid,
            email: decodeURIComponent(email || ''),
            name: decodeURIComponent(name || ''),
            picture: decodeURIComponent(picture || ''),
            merged: merged
          });

          // ✅ 수정: 구글 로그인 정보를 Firestore에 저장
          if (!merged) {
            // 새 계정이거나 연동되지 않은 경우에만 저장
            try {
              const { saveUserToFirestore } = await import('../utils');
              await saveUserToFirestore(userCredential.user, {
                loginMethod: 'google',
                email: decodeURIComponent(email || ''),
                displayName: decodeURIComponent(name || ''),
                firstName: decodeURIComponent(given_name || ''),
                lastName: decodeURIComponent(family_name || ''),
                photoURL: decodeURIComponent(picture || ''),
                locale: decodeURIComponent(locale || ''),
                emailVerified: email_verified,
                googleProfile: {
                  google_id: google_id,
                  email: decodeURIComponent(email || ''),
                  name: decodeURIComponent(name || ''),
                  given_name: decodeURIComponent(given_name || ''),
                  family_name: decodeURIComponent(family_name || ''),
                  picture: decodeURIComponent(picture || ''),
                  locale: decodeURIComponent(locale || ''),
                  email_verified: email_verified
                }
              });
              console.log('✅ 구글 로그인 정보 Firestore 저장 완료');
            } catch (saveError) {
              console.error('❌ Firestore 저장 실패:', saveError);
              // 저장 실패해도 로그인은 계속 진행
            }
          }
          
          // URL 파라미터 제거
          window.history.replaceState({}, document.title, window.location.pathname);

          // Google Analytics 로그인 이벤트 추적
          try {
            const { trackLogin } = await import('../utils');
            trackLogin(merged ? 'google_linked' : 'google');
          } catch (error) {
            console.warn('Failed to track Google login:', error);
          }

          if (merged) {
            console.log('✅ 기존 계정에 Google OAuth가 연동되었습니다!');
            showAlert('success', '연동 완료', '기존 계정에 구글 로그인이 연동되었습니다! 이제 구글로도 로그인할 수 있습니다.');
          } else {
            // 일반 로그인 성공 시에는 바로 이동
            navigate('/');
          }
        }
      } catch (error) {
        console.error('Google 로그인 처리 중 오류:', error);
        
        // 🔧 에러 시에도 로딩 스피너 종료
        if (setAuthLoading) {
          setAuthLoading(false);
          console.log('🔄 [GoogleCallback] 구글 로그인 로딩 스피너 종료 (에러)');
        }
        
        showAlert('error', '로그인 처리 오류', 'Google 로그인 처리 중 오류가 발생했습니다: ' + error.message);
      }
    };

    handleCallback();
  }, [navigate, location]);

  return (
    <>
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography>Google 로그인 처리 중...</Typography>
      </Box>

      {/* AlertDialog (랜딩페이지의 resendSuccessModal 패턴과 동일) */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        type={alertDialog.type}
        title={alertDialog.title}
        message={alertDialog.message}
        confirmText="확인"
      />
    </>
  );
};

export default GoogleCallback; 